<script lang="ts" setup>
import { Ref } from 'vue'
import WidgetWizardBackButton from '~/components/widget/wizard/back-button/WidgetWizardBackButton.vue'
export interface WidgetWizardProps {
    apiUrl?: string
    headingLevel?: string
    classNames?: string
    data?: WizardData | null
}

const props = withDefaults(defineProps<WidgetWizardProps>(), {
    headingLevel: 'h3',
    classNames: 'px-5 pt-15 pb-5 pb-md-15 px-md-20',
    data: null,
    apiUrl: undefined,
})

const wizard = useWizard()
const closeButton = inject<Ref<HTMLButtonElement>>('modalCloseButton')

const handleCloseButtonTabClick = (e: KeyboardEvent) => {
    const { key, shiftKey } = e
    if (key === 'Tab') {
        e.preventDefault()
        closeButton?.value.blur()
        if (shiftKey) {
            wizard.focusLastItem()
            return
        }

        if (!wizard.previousExists()) {
            wizard.focusFirstItem()
            return
        }

        wizard.setBackButtonFocusState(true)
        return
    }
}

onMounted(async () => {
    closeButton?.value?.addEventListener('keydown', handleCloseButtonTabClick)
    // get data
    if (props.data) {
        wizard.setCurrent(props.data)
        return
    }

    if (props.apiUrl) {
        const wizardData = await $fetch<WizardReponseData>(props.apiUrl)
        wizard.setCurrent({
            title: wizardData.title,
            children: wizardData.data,
        })
    }
})

onUnmounted(() => {
    closeButton?.value?.removeEventListener(
        'keydown',
        handleCloseButtonTabClick
    )
    wizard.reset()
})
</script>

<template>
  <div
    aria-polite="live"
    tabindex="0"
    class="border-0"
    style="outline: none"
    @click.stop="wizard.resetCurrentIndex()"
  >
    <section
      v-if="wizard.data.value.current"
      class="position-relative"
      :class="props.classNames"
    >
      <Tracker
        :key="wizard.data.value.current.title"
        :data="{ title: wizard.data.value.current.title }"
      >
        <WidgetWizardBackButton />
        <slot
          name="heading"
          :title="wizard.data.value.current.title"
        >
          <Heading
            :level="props.headingLevel"
            class="text-center mb-10"
          >
            {{ wizard.data.value.current.title }}
          </Heading>
        </slot>

        <ul
          v-if="wizard.data.value.current?.children?.length > 0"
          :key="wizard.data.value.current.title"
          class="row g-6"
        >
          <li
            v-for="(item, itemIndex) in wizard.data.value.current
              .children"
            :key="`${item.title}-${itemIndex}`"
            :class="{
              'col-12 col-md-6 col-lg-3':
                !item.bubble || Array.isArray(item.bubble),
              'col-6 flex-grow-1':
                item.bubble && !Array.isArray(item.bubble),
            }"
          >
            <WidgetWizardItem
              :data="item"
              :index="itemIndex"
              @close="closeButton?.click()"
            />
          </li>
        </ul>
      </Tracker>
    </section>
  </div>
</template>

<style lang="scss" scoped>
@import './WidgetWizard.scss';
</style>
